// Unified "My Account" summary for /portal/account.
// Shows credits balance, CV entitlement, subscription, brand installment
// outstanding/paid totals, and recent orders. Auto-refreshes after PayFast
// redirects (the user lands here after successful payments).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Wallet, FileText, CalendarClock, Receipt, CreditCard, ExternalLink } from "lucide-react";
import { myFinancialSummary } from "@/lib/api/accounting.functions";
import { listMyPayments } from "@/lib/api/portal.functions";


export const Route = createFileRoute("/_authenticated/portal/account")({
  head: () => ({ meta: [{ title: "My account · Nkadimeng Danny" }] }),
  component: AccountPage,
});

function rand(c: number): string {
  return `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function randNum(n: number): string {
  return `R${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function AccountPage() {
  const run = useServerFn(myFinancialSummary);
  const runPayments = useServerFn(listMyPayments);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["my-financial-summary"],
    queryFn: () => run(),
    refetchInterval: 30_000,
  });
  const paymentsQ = useQuery({
    queryKey: ["my-payments-account"],
    queryFn: () => runPayments(),
    refetchInterval: 60_000,
  });


  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your account…
      </div>
    );
  }
  if (isError) {
    return <div className="p-6 text-sm text-red-600">Could not load your account: {(error as any)?.message}</div>;
  }
  if (!data) return null;

  const credAny = (data.credits as any) ?? {};
  const balance = credAny?.balance ?? 0;
  const sub = data.subscription as any;
  const cv = data.cv_entitlement as any;


  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy md:text-3xl">My account</h1>
          <p className="text-sm text-muted-foreground">
            Signed in as <span className="font-medium">{data.profile.email || "—"}</span>
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-xs text-foreground hover:bg-accent disabled:opacity-50"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {/* Top KPIs */}
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi icon={<Wallet className="h-4 w-4" />} label="AI credits balance" value={String(balance)} sub={credAny?.updated_at ? `Updated ${new Date(credAny.updated_at).toLocaleString("en-ZA")}` : "Top-up anytime"} />
        <Kpi
          icon={<CalendarClock className="h-4 w-4" />}
          label="Outstanding payments"
          value={rand(data.installments.outstanding_cents)}
          sub={`${data.installments.plans.length} plan(s) · ${rand(data.installments.paid_lifetime_cents)} paid lifetime`}
          accent={data.installments.outstanding_cents > 0 ? "amber" : "emerald"}
        />
        <Kpi
          icon={<CreditCard className="h-4 w-4" />}
          label="Subscription"
          value={
            sub?.status === "active" && sub?.current_period_end && new Date(sub.current_period_end) > new Date()
              ? `${sub.plan_code ?? "Active"} · Subscribed`
              : sub?.plan_code ?? "None"
          }
          sub={sub?.status ? `${sub.status}${sub.current_period_end ? " · renews " + new Date(sub.current_period_end).toLocaleDateString("en-ZA") : ""}` : "No active membership"}
          accent={sub?.status === "active" && sub?.current_period_end && new Date(sub.current_period_end) > new Date() ? "emerald" : undefined}
        />
        <Kpi
          icon={<FileText className="h-4 w-4" />}
          label="CV builder"
          value={
            sub?.status === "active" && sub?.current_period_end && new Date(sub.current_period_end) > new Date()
              ? "Unlimited"
              : cv ? `${Math.max(0, (cv.paid_credits ?? 0) - (cv.paid_credits_used ?? 0))} left` : "—"
          }
          sub={
            sub?.status === "active" && sub?.current_period_end && new Date(sub.current_period_end) > new Date()
              ? "CVs, cover & motivational letters + premium templates"
              : cv ? `${cv.total_generated ?? 0} generated · free ${cv.free_used ? "used" : "available"}` : "Use a CV credit"
          }
        />

      </div>

      {/* Installment plans */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg text-navy">Installment plans</h2>
          <span className="text-xs text-muted-foreground">
            Payments made via our PayFast portal update here automatically.
          </span>
        </div>
        {data.installments.plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No installment plans on your email yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-right">Outstanding</th>
                  <th className="px-3 py-2 text-left">Next due</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.installments.plans.map((p: any) => {
                  const remaining = Math.max(0, p.total_amount_cents - p.paid_amount_cents);
                  return (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">{p.reference}</td>
                      <td className="px-3 py-2 text-right">{rand(p.total_amount_cents)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{rand(p.paid_amount_cents)}</td>
                      <td className={`px-3 py-2 text-right font-medium ${remaining > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                        {rand(remaining)}
                      </td>
                      <td className="px-3 py-2">{p.next_due_date ? new Date(p.next_due_date).toLocaleDateString("en-ZA") : "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${p.status === "settled" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.status !== "settled" && p.public_token ? (
                          <a
                            href={`/installments/${p.reference}?t=${p.public_token}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md bg-navy px-2.5 py-1 text-xs font-medium text-white hover:bg-navy/90"
                          >
                            Pay now <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent orders */}
      <section className="grid gap-3 md:grid-cols-2">
        <OrderList title="Recent credit top-ups" rows={data.orders.credit_orders} />
        <OrderList title="Recent CV orders" rows={data.orders.cv_orders} />
      </section>

      {/* Payment history — all PayFast payments (subscription, CV, credits, contracts) */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg text-navy">Payment history</h2>
          <Link to="/portal/my-payments" className="text-xs text-navy underline">
            Manage receipts →
          </Link>
        </div>
        {paymentsQ.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading payments…</p>
        ) : (paymentsQ.data?.payments ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments on record yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">PayFast ID</th>
                </tr>
              </thead>
              <tbody>
                {(paymentsQ.data?.payments ?? []).slice(0, 15).map((p: any) => {
                  const isPaid = String(p.status).toLowerCase() === "paid";
                  const when = p.paid_at ?? p.created_at;
                  return (
                    <tr key={`${p.kind}-${p.reference}`} className="border-t border-border">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {when ? new Date(when).toLocaleDateString("en-ZA") : "—"}
                      </td>
                      <td className="px-3 py-2 capitalize">{p.kind}</td>
                      <td className="px-3 py-2">
                        <span className="block truncate max-w-[260px]" title={p.title}>{p.title}</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{p.reference}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{randNum(Number(p.amount_zar ?? 0))}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                          isPaid
                            ? "bg-emerald-100 text-emerald-800"
                            : String(p.status).toLowerCase() === "failed"
                              ? "bg-rose-100 text-rose-800"
                              : "bg-amber-100 text-amber-800"
                        }`}>
                          {isPaid ? "Confirmed" : (p.status || "pending")}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">
                        {p.pf_payment_id ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>


      <div className="rounded-xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
        <Receipt className="mr-1 inline h-3.5 w-3.5" />
        Payments made on our website are credited automatically. If you paid by EFT, allow our team a few hours to confirm and update your balance.{" "}
        <Link to="/portal/my-payments" className="text-navy underline">View all payments →</Link>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: string; sub: string; accent?: "amber" | "emerald" }) {
  const tone = accent === "amber"
    ? "border-amber-200 bg-amber-50"
    : accent === "emerald"
      ? "border-emerald-200 bg-emerald-50"
      : "border-border bg-card";
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </div>
      <div className="mt-1 font-display text-2xl text-navy">{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function OrderList({ title, rows }: { title: string; rows: any[] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="mb-2 font-display text-base text-navy">{title}</h3>
      {(rows ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">No records yet.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {rows.slice(0, 8).map((r: any) => (
            <li key={r.reference} className="flex items-center justify-between gap-2">
              <span className="truncate">
                <span className="font-mono text-[11px] text-muted-foreground">{r.reference}</span>
                {" · "}
                <span className="capitalize">{r.status}</span>
              </span>
              <span className="font-medium">{randNum(Number(r.amount_zar ?? 0))}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
